import { Button, CircularProgress, InputAdornment } from '@mui/material';
import classNames from 'classnames';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import queryString from 'query-string';
import { useCallback, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useForm } from 'react-hook-form';

import { ReactComponent as HidePasswordIcon } from '../../../assets/icons/Login/passwordHideIcon.svg';
import { ReactComponent as ShowPasswordIcon } from '../../../assets/icons/Login/passwordShowIcon.svg';
import TextField from '../../../components/Inputs/TextField';
import useQueryParam from '../../../hooks/useCustomQueryParam';
import useMixpanel from '../../../hooks/useMixpanel';
import { useLoginService } from '../../../services/loginService';
import ForgotPasswordLink from '../ForgotPasswordContent/ForgotPasswordLink';
import { LoginSuggestionsList } from '../LoginSuggestionsList';
import { useLoginSuggestions } from '../useLoginSuggestions';
import { useRecentLoginValues } from '../useRecentLoginValues';
import styles from './index.module.scss';

// TODO: mix panel EVERYTHING

interface ILoginProps {
  username: string;
  password: string;
}

function LoginForm() {
  const [preFillData] = useQueryParam<string>('pre_fill');
  const searchParams = queryString.parse(window.location.search);
  const username = searchParams?.username as string;

  const { recentValues: recentUsernames, saveValue: saveUsername } =
    useRecentLoginValues('auth.recentUsernames');

  const {
    register,
    trigger,
    clearErrors,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors, isValid },
  } = useForm<ILoginProps>({
    reValidateMode: 'onChange',
    values: { username, password: '' },
  });

  const { login, state, redirectToApp } = useLoginService({
    onFail(err) {
      console.error(err);
    },
    onFinish(idToken) {
      saveUsername(getValues('username'));
      if (preFillData && isMobile) {
        state.setIsWelcome(true);
        state.setIdToken(idToken);
      } else {
        redirectToApp({ idToken });
      }
    },
  });

  const [isShowPassword, setIsShowPassword] = useState(false);

  const { trackClick, track } = useMixpanel();

  const usernameRegistration = register('username', { required: 'Please enter user name' });

  const currentUsername = watch('username');
  const { suggestions, isOpen, selectSuggestion, onFocus, onBlur } = useLoginSuggestions({
    fieldName: 'username',
    currentValue: currentUsername,
    recentValues: recentUsernames,
    setValue,
    onFieldBlur: usernameRegistration.onBlur,
  });

  const onLogin = useCallback(
    (data: ILoginProps) => {
      trackClick('start_login');
      login(data);
    },
    [trackClick, login],
  );

  useEffect(() => {
    if (preFillData) {
      const data = JSON.parse(preFillData);
      track('first_login_prefill_data');
      onLogin(data);
    }
  }, [preFillData]);

  return (
    <form onSubmit={handleSubmit(onLogin)}>
      <LoginSuggestionsList
        id="username-suggestions"
        open={isOpen}
        suggestions={suggestions}
        currentValue={currentUsername}
        onSelect={selectSuggestion}
      >
        <TextField
          label="Username"
          wrapperClassName={styles.inputWrapper}
          error={!!errors?.username}
          helperText={errors?.username?.message}
          inputClassName={styles.inputField}
          hideErrorIcon
          autoFocus={false}
          autoComplete={recentUsernames.length ? 'off' : 'username'}
          data-cy="username"
          variant="filled"
          InputLabelProps={{ classes: { shrink: styles.shrink } }}
          inputProps={{
            style: { fontSize: 16 },
            role: 'combobox',
            'aria-autocomplete': 'list',
            'aria-expanded': isOpen,
            'aria-controls': 'username-suggestions',
            'aria-haspopup': 'listbox',
          }}
          {...usernameRegistration}
          onFocus={onFocus}
          onBlur={onBlur}
        />
      </LoginSuggestionsList>
      <TextField
        label="Password"
        wrapperClassName={styles.inputWrapper}
        error={!!errors?.password}
        helperText={errors?.password?.message}
        inputClassName={styles.inputField}
        type={isShowPassword ? 'text' : 'password'}
        hideErrorIcon
        autoFocus={false}
        InputLabelProps={{ classes: { shrink: styles.shrink } }}
        data-cy="password"
        autoComplete="new-password"
        variant="filled"
        {...register('password', {
          required: 'Please enter password',
          pattern: {
            value: /(?=.{8,})/,
            message: 'Password should include at least 8 characters.',
          },
        })}
        onBlur={() => trigger('password')}
        onInput={() => clearErrors('password')}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" className={styles.endAdornment}>
              <div onClick={() => setIsShowPassword(!isShowPassword)} className="p-0">
                {isShowPassword ? <HidePasswordIcon /> : <ShowPasswordIcon />}
              </div>
            </InputAdornment>
          ),
        }}
      />
      <ForgotPasswordLink />
      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={!isValid}
        data-cy="login-button"
        className={clsx(styles.submitBtn, 'rounded-4')}
      >
        {state.isLoading ? <CircularProgress size={15} /> : 'Login'}
      </Button>
      {state.errorMessage ? (
        <div className={classNames(styles.error, 'absolute')}>{state.errorMessage}</div>
      ) : null}
    </form>
  );
}
export default observer(LoginForm);
